import { Ionicons } from "@expo/vector-icons";
import { useIsFocused, useRouter } from "expo-router";
import { Platform, Pressable, ScrollView, Text, View } from "react-native";
import {
  Button,
  ErrorState,
  LoadingSpinner,
  Money,
  WebHead,
} from "../../components";
import { useColors } from "../../lib/colors";
import { trpc } from "../../lib/trpc";
import { formatCurrency, formatDate } from "../../lib/utils";
import {
  buildBalanceSheetSnapshot,
  type BalanceSheetStatementSection,
} from "../../lib/balance-sheet-statement";
import { useScreenReadyTelemetry } from "../../hooks/useScreenReadyTelemetry";

const DISPLAY_FONT = "InstrumentSerif_400Regular";

function getHouseholdName(
  persons:
    | Array<{ firstName: string | null; lastName: string | null }>
    | undefined,
): string {
  const names = (persons ?? [])
    .map((person) =>
      [person.firstName, person.lastName].filter(Boolean).join(" ").trim(),
    )
    .filter(Boolean);

  if (names.length === 0) return "Per Diem household";
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} & ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} & ${names.at(-1)}`;
}

function StatementSection({
  section,
}: {
  section: BalanceSheetStatementSection;
}) {
  return (
    <View
      nativeID={`financial-statement-section-${section.key}`}
      className="mb-4"
    >
      <View className="flex-row items-end justify-between border-b border-[#173D2A] pb-1.5">
        <Text className="text-[10px] font-bold uppercase tracking-[1.3px] text-[#173D2A]">
          {section.title}
        </Text>
        <Money weight={500} className="text-[10px] text-[#173D2A]">
          {formatCurrency(section.total, { decimals: 0 })}
        </Money>
      </View>
      <View className="pt-1">
        {section.lines.map((line) => (
          <View
            key={line.id}
            className="flex-row items-start justify-between gap-3 py-1.5"
          >
            <View className="min-w-0 flex-1">
              <Text className="text-[10.5px] leading-[14px] text-[#202720]">
                {line.label}
              </Text>
              {line.detail ? (
                <Text className="mt-0.5 text-[8.5px] leading-[11px] text-[#747B74]">
                  {line.detail}
                </Text>
              ) : null}
            </View>
            <Money className="text-[10.5px] leading-[14px] text-[#202720]">
              {formatCurrency(line.amount, { decimals: 0 })}
            </Money>
          </View>
        ))}
      </View>
    </View>
  );
}

function StatementColumn({
  title,
  sections,
  total,
  emptyLabel,
}: {
  title: string;
  sections: BalanceSheetStatementSection[];
  total: number;
  emptyLabel: string;
}) {
  return (
    <View className="min-w-0 flex-1">
      <View className="mb-4 flex-row items-baseline justify-between border-b-2 border-[#173D2A] pb-2">
        <Text className="text-[16px] font-semibold text-[#173D2A]">
          {title}
        </Text>
        <Money weight={500} className="text-[16px] text-[#173D2A]">
          {formatCurrency(total, { decimals: 0 })}
        </Money>
      </View>
      {sections.length > 0 ? (
        sections.map((section) => (
          <StatementSection key={section.key} section={section} />
        ))
      ) : (
        <Text className="py-3 text-[10.5px] text-[#747B74]">{emptyLabel}</Text>
      )}
    </View>
  );
}

export default function FinancialStatementScreen() {
  const router = useRouter();
  const isFocused = useIsFocused();
  const colors = useColors();
  const householdQ = trpc.households.get.useQuery(undefined, {
    enabled: isFocused,
  });
  const accountsQ = trpc.accounts.list.useQuery(undefined, {
    enabled: isFocused,
  });
  const assetsQ = trpc.assets.list.useQuery(undefined, { enabled: isFocused });
  const liabilitiesQ = trpc.liabilities.list.useQuery(undefined, {
    enabled: isFocused,
  });
  const taxEstimateQ = trpc.tax.currentYearEstimate.useQuery(undefined, {
    enabled: isFocused,
  });

  const coreReady =
    householdQ.data != null &&
    accountsQ.data != null &&
    assetsQ.data != null &&
    liabilitiesQ.data != null;
  useScreenReadyTelemetry({
    ready: coreReady,
    screenName: "financial-statement",
  });

  if (
    householdQ.isLoading ||
    accountsQ.isLoading ||
    assetsQ.isLoading ||
    liabilitiesQ.isLoading ||
    taxEstimateQ.isLoading
  ) {
    return <LoadingSpinner message="Preparing your statement..." />;
  }

  const queryError =
    householdQ.error ?? accountsQ.error ?? assetsQ.error ?? liabilitiesQ.error;
  if (queryError) {
    return (
      <ErrorState
        error={queryError}
        screen="financial-statement"
        metadata={{
          failedQueries:
            "households.get,accounts.list,assets.list,liabilities.list",
        }}
        onRetry={() => {
          void Promise.allSettled([
            householdQ.refetch(),
            accountsQ.refetch(),
            assetsQ.refetch(),
            liabilitiesQ.refetch(),
            taxEstimateQ.refetch(),
          ]);
        }}
      />
    );
  }

  const generatedAt = new Date();
  const householdName = getHouseholdName(householdQ.data?.persons);
  const statement = buildBalanceSheetSnapshot({
    accounts: accountsQ.data ?? [],
    assets: assetsQ.data ?? [],
    liabilities: liabilitiesQ.data ?? [],
    taxRefundOrOwe: taxEstimateQ.data?.result.refundOrOwe ?? 0,
  });

  const printStatement = () => {
    if (Platform.OS === "web" && typeof globalThis.print === "function") {
      globalThis.print();
    }
  };

  return (
    <>
      <WebHead>
        <title>Personal Financial Statement – Per Diem</title>
      </WebHead>
      <ScrollView
        nativeID="financial-statement-screen"
        className="flex-1 bg-[#EAE8E1] dark:bg-[#0F1411]"
        contentContainerStyle={{ padding: 24, alignItems: "center" }}
      >
        <View
          nativeID="financial-statement-print-root"
          className="w-full items-center"
        >
          <View
            nativeID="financial-statement-print-actions"
            className="mb-4 w-full max-w-[816px] flex-row items-center justify-between"
          >
            <Pressable
              onPress={() => router.push("/(app)/accounts")}
              className="flex-row items-center gap-2 rounded-xl px-3 py-2 active:opacity-70"
              accessibilityRole="button"
              accessibilityLabel="Back to Balance Sheet"
            >
              <Ionicons
                name="arrow-back"
                size={18}
                color={colors.textSecondary}
              />
              <Text className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                Balance Sheet
              </Text>
            </Pressable>
            {Platform.OS === "web" ? (
              <Button size="sm" onPress={printStatement}>
                Print / Save as PDF
              </Button>
            ) : (
              <Text className="text-xs text-gray-500">
                Printing is available on the web app.
              </Text>
            )}
          </View>

          <View
            nativeID="financial-statement-document"
            className="w-full max-w-[816px] bg-[#FFFEFA] px-10 py-9 shadow-xl"
            style={{ minHeight: 1008 }}
          >
            <View className="flex-row items-start justify-between gap-6 border-b border-[#B8BDB5] pb-5">
              <View className="min-w-0 flex-1">
                <Text className="text-[9px] font-bold uppercase tracking-[2.2px] text-[#2B7A50]">
                  Per Diem
                </Text>
                <Text
                  className="mt-2 text-[35px] leading-[39px] text-[#173D2A]"
                  style={{ fontFamily: DISPLAY_FONT }}
                >
                  Personal Financial Statement
                </Text>
                <Text className="mt-2 text-[12px] text-[#515951]">
                  {householdName}
                </Text>
              </View>
              <View className="items-end pt-1">
                <Text className="text-[8px] font-bold uppercase tracking-[1.1px] text-[#747B74]">
                  As of
                </Text>
                <Text className="mt-1 text-[11px] font-semibold text-[#202720]">
                  {formatDate(generatedAt, "long")}
                </Text>
              </View>
            </View>

            <View className="my-5 flex-row border-y border-[#D8DAD4] py-3.5">
              <View className="flex-1 border-r border-[#D8DAD4] pr-5">
                <Text className="text-[8px] font-bold uppercase tracking-[1.2px] text-[#747B74]">
                  Total assets
                </Text>
                <Money
                  weight={500}
                  className="mt-1.5 text-[19px] text-[#202720]"
                >
                  {formatCurrency(statement.totalAssets, { decimals: 0 })}
                </Money>
              </View>
              <View className="flex-1 border-r border-[#D8DAD4] px-5">
                <Text className="text-[8px] font-bold uppercase tracking-[1.2px] text-[#747B74]">
                  Total liabilities
                </Text>
                <Money
                  weight={500}
                  className="mt-1.5 text-[19px] text-[#202720]"
                >
                  {formatCurrency(statement.totalLiabilities, { decimals: 0 })}
                </Money>
              </View>
              <View className="flex-1 pl-5">
                <Text className="text-[8px] font-bold uppercase tracking-[1.2px] text-[#2B7A50]">
                  Net worth
                </Text>
                <Money
                  weight={500}
                  className="mt-1.5 text-[19px] text-[#173D2A]"
                >
                  {formatCurrency(statement.netWorth, { decimals: 0 })}
                </Money>
              </View>
            </View>

            <View
              nativeID="financial-statement-columns"
              className="flex-col gap-7 md:flex-row"
            >
              <StatementColumn
                title="Assets"
                sections={statement.assetSections}
                total={statement.totalAssets}
                emptyLabel="No assets recorded."
              />
              <StatementColumn
                title="Liabilities"
                sections={statement.liabilitySections}
                total={statement.totalLiabilities}
                emptyLabel="No liabilities recorded."
              />
            </View>

            <View className="mt-auto flex-row items-center justify-between gap-6 border-t border-[#D8DAD4] pt-3">
              <Text className="text-[8.5px] leading-[11px] text-[#515951]">
                Know what you can spend today — and still retire early.
              </Text>
              <Text className="text-[9px] font-bold tracking-[0.6px] text-[#2B7A50]">
                perdiem.app
              </Text>
            </View>
          </View>
        </View>
      </ScrollView>
    </>
  );
}
